import * as voiceService from '../service'
import type { VoiceNotesState } from './state'
import type { VoiceEntityType, SessionStatus } from '../types'

export const createActions = (state: VoiceNotesState) => {
  const fetchAllSessions = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      state.sessions = await voiceService.fetchSessions()
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar sesiones'
      console.error('Error fetching voice sessions:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchSessionsByEntity = async (
    entityType: VoiceEntityType,
    entityId: string,
  ): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const params = entityType === 'project'
        ? { projectId: entityId }
        : entityType === 'application'
          ? { applicationId: entityId }
          : { meetingId: entityId }

      state.sessions = await voiceService.fetchSessions(params)
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error al cargar sesiones'
      console.error('Error fetching voice sessions:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchFragments = async (sessionId: number): Promise<void> => {
    state.isLoadingFragments = true

    try {
      state.fragments = await voiceService.fetchFragments(sessionId)
      state.currentSession = state.sessions.find((s) => s.id === sessionId) || null
    } catch (error) {
      console.error('Error fetching fragments:', error)
      state.error = error instanceof Error ? error.message : 'Error al cargar fragmentos'
    } finally {
      state.isLoadingFragments = false
    }
  }

  const updateSession = async (
    sessionId: number,
    data: Partial<{ name: string; status: SessionStatus; description: string; tags: string }>,
  ): Promise<void> => {
    await voiceService.updateSession(sessionId, data)

    const idx = state.sessions.findIndex((s) => s.id === sessionId)
    if (idx !== -1) {
      state.sessions[idx] = { ...state.sessions[idx], ...data }
    }
    if (state.currentSession?.id === sessionId) {
      state.currentSession = { ...state.currentSession, ...data }
    }
  }

  const deleteSession = async (sessionId: number): Promise<void> => {
    await voiceService.deleteSession(sessionId)
    state.sessions = state.sessions.filter((s) => s.id !== sessionId)
    if (state.currentSession?.id === sessionId) {
      state.currentSession = null
      state.fragments = []
    }
  }

  const updateFragment = async (fragmentId: number, text: string): Promise<void> => {
    await voiceService.updateFragment(fragmentId, text)

    const idx = state.fragments.findIndex((f) => f.id === fragmentId)
    if (idx !== -1) {
      state.fragments[idx] = { ...state.fragments[idx], text }
    }
  }

  const deleteFragment = async (fragmentId: number): Promise<void> => {
    await voiceService.deleteFragment(fragmentId)
    state.fragments = state.fragments.filter((f) => f.id !== fragmentId)

    // Update fragment count in session
    if (state.currentSession) {
      state.currentSession = {
        ...state.currentSession,
        fragmentCount: state.currentSession.fragmentCount - 1,
      }
      const idx = state.sessions.findIndex((s) => s.id === state.currentSession!.id)
      if (idx !== -1) {
        state.sessions[idx] = { ...state.sessions[idx], fragmentCount: state.sessions[idx].fragmentCount - 1 }
      }
    }
  }

  const clearFragments = (): void => {
    state.currentSession = null
    state.fragments = []
    state.analysis = null
  }

  const fetchAnalysis = async (sessionId: number): Promise<void> => {
    state.isLoadingAnalysis = true
    try {
      state.analysis = await voiceService.fetchAnalysis(sessionId)
    } catch (error) {
      console.error('Error fetching analysis:', error)
      state.analysis = null
    } finally {
      state.isLoadingAnalysis = false
    }
  }

  const analyzeSession = async (sessionId: number, prompt?: string): Promise<void> => {
    state.isAnalyzing = true
    try {
      state.analysis = await voiceService.analyzeSession(sessionId, prompt)
    } catch (error) {
      console.error('Error analyzing session:', error)
      throw error
    } finally {
      state.isAnalyzing = false
    }
  }

  const analyzeCustom = async (sessionId: number, prompt: string): Promise<{ customPrompt: string; customResult: string }> => {
    state.isAnalyzing = true
    try {
      const result = await voiceService.analyzeCustom(sessionId, prompt)
      if (state.analysis) {
        state.analysis = { ...state.analysis, customPrompt: result.customPrompt, customResult: result.customResult }
      }
      return result
    } catch (error) {
      console.error('Error analyzing custom:', error)
      throw error
    } finally {
      state.isAnalyzing = false
    }
  }

  const deleteAnalysis = async (sessionId: number): Promise<void> => {
    await voiceService.deleteAnalysis(sessionId)
    state.analysis = null
  }

  return {
    fetchAllSessions,
    fetchSessionsByEntity,
    fetchFragments,
    updateSession,
    deleteSession,
    updateFragment,
    deleteFragment,
    clearFragments,
    fetchAnalysis,
    analyzeSession,
    analyzeCustom,
    deleteAnalysis,
  }
}
